import type { AxiosError } from 'axios';
import { HttpStatusCode } from 'axios';

import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';

import type { LogoutUseCase } from '@contexts/auth/application/use-cases/logout.usecase';
import type { RefreshSessionUseCase } from '@contexts/auth/application/use-cases/refresh-session.usecase';
import type { RevertSuplantUseCase } from '@contexts/auth/application/use-cases/revert-suplant.usecase';
import { hasValue, type HttpInterceptor, type StorageRepository } from '@contexts/shared/domain';
import type { GetLoggedUserUseCase } from '@contexts/users/application';
import type { User } from '@contexts/users/domain';

import {
	LOGOUT_USE_CASE,
	REFRESH_SESSION_USE_CASE,
	REVERT_SUPLANT_USE_CASE
} from '@dependencies/auth';
import { HTTP_INTERCEPTOR, STORAGE_REPOSITORY } from '@dependencies/shared';
import { GET_LOGGED_USER_USE_CASE } from '@dependencies/users';

import { routesMetadata } from '@shared/models';
import { WebsocketService } from '@shared/services/websocket.service';

@Injectable()
export class AuthenticationService {
	private readonly router: Router = inject(Router);
	private readonly httpInterceptor: HttpInterceptor = inject(HTTP_INTERCEPTOR);

	private readonly getLoggedUserUseCase: GetLoggedUserUseCase = inject(GET_LOGGED_USER_USE_CASE);

	private readonly logoutUseCase: LogoutUseCase = inject(LOGOUT_USE_CASE);
	private readonly refreshSessionUseCase: RefreshSessionUseCase =
		inject(REFRESH_SESSION_USE_CASE);
	private readonly revertSuplantUseCase: RevertSuplantUseCase = inject(REVERT_SUPLANT_USE_CASE);

	private readonly websocketService: WebsocketService = inject(WebsocketService);
	private readonly storageRepository: StorageRepository = inject(STORAGE_REPOSITORY);

	private readonly innerUser = signal<User | null>(null);
	public user = this.innerUser.asReadonly();
	private isUserLoggedOut = false;
	private isRedirectingToHome = false;
	private userCheckPromise: Promise<User> | null = null;
	private sessionRefreshPromise: Promise<void> | null = null;

	constructor() {
		this.httpInterceptor.interceptUnauthorized(this.renewSessionOnUnauthorized.bind(this));
		this.httpInterceptor.interceptResponse(this.redirectUnauthorizedRequests.bind(this));
		this.websocketService.onAuthenticationFailure(this.renewSession.bind(this));
	}

	/**
	 * Requests that must never trigger a session renewal: either the user is not
	 * authenticated yet, or the renewal itself is the failing request.
	 */
	private static readonly PRE_AUTH_PATHS = [
		'/auth/login',
		'/auth/twofa/login',
		'/auth/passkeys/authenticate',
		'/auth/refresh',
		'/auth/logout',
		'/auth/totp/secret',
		'/auth/totp/code',
		'/auth/recovery-password',
		'/auth/reset-password',
		'/users/activate',
		'/users/revert-activation',
		'/users/status'
	];

	/**
	 * Requests whose 401 must not log the user out. `/auth/me` is renewable —the
	 * guard resolves it on its own— so it only belongs to this second list.
	 */
	private static readonly UNAUTHENTICATED_PATHS = [
		'/auth/me',
		...AuthenticationService.PRE_AUTH_PATHS
	];

	/**
	 * A refresh token is single use: the backend rotates it on every renewal and
	 * answers 409 when two renewals race, keeping the session alive. Only a 401 is
	 * terminal, and it is the response interceptor that ends up logging the user out.
	 */
	private static readonly REFRESH_CONFLICT_RETRIES = 2;
	private static readonly REFRESH_CONFLICT_DELAY_IN_MILLISECONDS = 250;

	private isUnauthenticatedPath(url: string): boolean {
		return AuthenticationService.UNAUTHENTICATED_PATHS.some((path) => url.includes(path));
	}

	private isPreAuthFlow(url: string): boolean {
		return AuthenticationService.PRE_AUTH_PATHS.some((path) => url.includes(path));
	}

	private async renewSessionOnUnauthorized(url: string): Promise<boolean> {
		if (this.isPreAuthFlow(url) || this.isUserLoggedOut) {
			return false;
		}

		return this.renewSession();
	}

	/**
	 * Single flight: concurrent 401s wait on the very same renewal, so the refresh
	 * token is rotated once instead of racing against itself.
	 */
	private async renewSession(): Promise<boolean> {
		if (this.isUserLoggedOut) {
			return false;
		}

		this.sessionRefreshPromise ??= this.rotateRefreshToken().finally(() => {
			this.sessionRefreshPromise = null;
		});

		try {
			await this.sessionRefreshPromise;
			return true;
		} catch {
			return false;
		}
	}

	private async rotateRefreshToken(attempt = 1): Promise<void> {
		try {
			await this.refreshSessionUseCase();
		} catch (error) {
			const status = (error as AxiosError).response?.status;
			const canRetry = attempt <= AuthenticationService.REFRESH_CONFLICT_RETRIES;

			if (status !== HttpStatusCode.Conflict || !canRetry) {
				throw error;
			}

			await new Promise((resolve) =>
				setTimeout(resolve, AuthenticationService.REFRESH_CONFLICT_DELAY_IN_MILLISECONDS)
			);

			return this.rotateRefreshToken(attempt + 1);
		}
	}

	private redirectUnauthorizedRequests(status: number, url: string): void {
		const isUnauthenticatedPath = this.isUnauthenticatedPath(url);

		if (
			status !== HttpStatusCode.TooManyRequests &&
			status === HttpStatusCode.Unauthorized &&
			!isUnauthenticatedPath &&
			!this.isUserLoggedOut
		) {
			if (this.user()?.suplant) {
				this.redirectToHome();
			} else {
				this.logout();
			}
		}

		if (status === HttpStatusCode.Unauthorized && url.includes('/auth/logout')) {
			this.isUserLoggedOut = true;
		}
	}

	private redirectToHome(): void {
		if (this.isRedirectingToHome) {
			return;
		}

		this.isRedirectingToHome = true;
		window.location.href = '/';
	}

	async isUserLogged(): Promise<boolean> {
		if (hasValue(this.user())) {
			return true;
		}

		this.userCheckPromise ??= this.refreshUser();

		try {
			await this.userCheckPromise;
			return true;
		} catch {
			return false;
		}
	}

	async login(): Promise<void> {
		await this.storageRepository.clearAll();
		this.innerUser.set(null);
		this.userCheckPromise = null;
		this.isUserLoggedOut = false;
		await this.refreshUser();
	}

	async logout(): Promise<void> {
		if (this.isUserLoggedOut) {
			return;
		}

		this.isUserLoggedOut = true;
		await this.logoutUseCase().catch(() => {});
		this.websocketService.disconnect();
		await this.storageRepository.clearAll();

		this.innerUser.set(null);
		this.userCheckPromise = null;
		this.sessionRefreshPromise = null;
		await this.router.navigateByUrl(routesMetadata.auth.absolute);
	}

	async refreshUser(): Promise<User> {
		const user = await this.getLoggedUserUseCase();
		this.innerUser.set(user);

		await this.websocketService.connect();

		return user;
	}
}
